// ---------------------------------------------------------------------------
// Home page copy.
//
// Kept out of the component so the claims can be reviewed on their own, which
// matters more here than anywhere else in the app: this is the page that
// tells someone what they are buying.
//
// EVERY LINE HERE HAS TO BE TRUE TODAY.
//
// Deliberately absent, and why:
//   · a seller count ("30,000+ sellers")   — nobody has counted
//   · a founder bio                        — not mine to write
//   · an uptime SLA / "enterprise-grade"   — nothing measures it
//   · "13+ tools"                          — there are four
//   · Amazon / Flipkart / Myntra as live   — built, not switched on. Said so.
// ---------------------------------------------------------------------------

// The four steps a seller actually goes through, in order.
export const HOW_IT_WORKS = [
  {
    step: '01',
    title: 'Add your product',
    blurb: 'A name and a photograph is enough to begin. Everything the marketplace asks for on top of '
      + 'that is either prepared for you or asked in plain words.'
  },
  {
    step: '02',
    title: 'Press Fix with AI',
    blurb: 'Four marketplace-ready images, an SEO title and a description are prepared for you, along with '
      + 'every category field the marketplace asks for.'
  },
  {
    step: '03',
    title: 'Answer what we cannot know',
    blurb: 'Your SKU and your stock. That is usually the whole of it — compliance details come from your '
      + 'settings, filled in once.'
  },
  {
    step: '04',
    title: 'Download and upload',
    blurb: 'A marketplace-formatted file, ready to upload as it is. Re-download it any time at no cost.'
  }
];

// What is genuinely in the product.
export const FEATURES = [
  {
    key: 'images',
    title: 'Four images per listing',
    blurb: 'Main, lifestyle, features and detail — prepared for the product, not stretched from one vendor photo.'
  },
  {
    key: 'copy',
    title: 'Titles and descriptions that vary',
    blurb: 'Every product will be Ai Generating, so two sellers of the same goods never publish '
      + 'identical copy.'
  },
  {
    key: 'fields',
    title: 'Category fields filled in',
    blurb: 'The marketplace decides which fields a category needs. We fill them from the product and your '
      + 'settings, then show you what is left.'
  },
  {
    key: 'catalogue',
    title: 'Products ready to list',
    blurb: 'Stock you can put on a marketplace without sourcing and photographing it yourself.'
  },
  {
    key: 'multi',
    title: 'Up to ten listings per product',
    blurb: 'One product, several listings, each with its own images and SKU — in a single download.'
  },
  {
    key: 'files',
    title: 'Files, not promises',
    blurb: 'You get the actual upload sheet. Nothing is published on your behalf and no marketplace password '
      + 'is ever asked for.'
  }
];

// The honest version of "problem & solution".
export const STRUGGLE = [
  'Retyping the same product into a different sheet for every marketplace',
  'Guessing which fields a category needs, then getting the file rejected',
  'Shooting or sourcing four usable images per product',
  'Writing a different title and description for every listing so they do not clash'
];

export const EDGE = [
  'The category fields are read from the marketplace, not guessed',
  'Four images per listing, prepared per product',
  'A different title and description each time, Ai Generated per product',
  'A finished file you upload yourself — you keep the account and the control'
];

// Who it is for. Kept to what the platform actually serves today.
export const AUDIENCE = [
  {
    title: 'Meesho sellers',
    blurb: 'The marketplace this exports to today, with its fee model built into the calculator.'
  },
  {
    title: 'Resellers & dropshippers',
    blurb: 'Put a range on sale without sourcing and photographing every item yourself.'
  },
  {
    title: 'Small brands',
    blurb: 'List a growing range without a cataloguing team or a photographer on call.'
  },
  {
    title: 'Catalogue services',
    blurb: 'Run listings for several sellers, with per-seller variation built in rather than bolted on.'
  }
];

export const FAQS = [
  {
    q: 'What does a listing actually cost?',
    a: 'Ten credits for a completed listing — the images, the title and description, and every marketplace '
      + 'field filled in. Preparing a listing and changing your mind before completing it are free, and so is '
      + 're-downloading one you have already completed.'
  },
  {
    q: 'Which marketplaces can I export to?',
    a: 'Meesho today. Amazon, Flipkart, Myntra and Shopify '
      + 'Coming Soon ! — we would rather say that than list them as live.'
  },
  {
    q: 'Do you publish to my marketplace account?',
    a: 'No, and we never ask for your marketplace password. You get the upload file and you upload it. That '
      + 'keeps your account, and every decision about it, with you.'
  },
  {
    q: 'Where do the images come from?',
    a: 'The images are generated on demand by Ai specifically for your product listing.'
  },
  {
    q: 'Will my listing look the same as another seller’s?',
    a: 'No. Images are Generate per seller, and the title and description also Generating by Ai '
      + 'for that product. The same content is never sent to the same seller twice.'
  },
  {
    q: 'Can I edit anything before I download?',
    a: 'All of it. The Ai assistant prepares the listing, and the full edit form is one click away from the '
      + 'moment it exists.'
  },
  {
    q: 'Do I need a card to start?',
    a: 'No. New accounts get free credits, which is enough to take a couple of listings all the way through '
      + 'and see the file you would be buying.'
  }
];

// ---------------------------------------------------------------------------
// The founder.
//
// This file used to list "a founder bio" under things deliberately absent,
// because it was not mine to write. It is here now because it was asked for —
// but under the same rule as everything else on this page: nothing that cannot
// be verified. No years of experience, no former employers, no revenue figures,
// no "ex-" anything. A name, a role, a link somebody can check, and the reason
// the product exists, which is the only part of a founder story a visiting
// seller actually cares about.
//
// If more detail belongs here, it should come from the founder, not from me.
// ---------------------------------------------------------------------------
export const FOUNDER = {
  name: 'Vishal Timbadiya',
  role: 'Founder',
  linkedin: 'https://www.linkedin.com/in/vishal-timbadiya',
  // Written in the founder's voice about the PRODUCT, which is checkable,
  // rather than about the person, which is not.
  note: 'SellerList AI started with a problem I kept running into: a seller finds a product they '
    + 'want to sell, and then loses days to the part that has nothing to do with selling — '
    + 'photographs, a title that fits the marketplace, a category tree, and Multiple required '
    + 'fields that are different on every platform.',
  note2: 'So the work happens once, for the product, instead of once per seller. You import it, '
    + 'the images and the copy are already prepared, and you download a file the marketplace '
    + 'accepts. That is the whole idea, and everything on this page is built around it.'
};

// ---------------------------------------------------------------------------
// The products shown in the hero, cycling one after another.
//
// FOUR real catalogue products, not one. A single frozen example made the page
// look like the platform contained exactly one product — which, for a
// catalogue tool, argues against the very thing being claimed.
//
// Every image is a real file on the storage host. The first two are drawn from
// their prepared four-slot tanks, which is literally what a seller publishes;
// the other two use the vendor's own photographs, because those products have
// not had a tank stocked yet. Nothing here is a mockup.
//
// SERVED BY THIS APP, FROM THIS DOMAIN.
//
// These used to be hotlinked from www.wealmery.com — the domain the product
// used to live on. Three things wrong with that: the marketing page depended
// on a domain being retired, it could not be redirected without breaking the
// page, and it told search engines the two sites were the same thing while we
// were trying to separate them.
//
// They are now part of the build, converted to WebP: 10.6MB of PNG became
// 964kB, which the home page was previously fetching over the network.
// ---------------------------------------------------------------------------
export const SHOWCASES = [
  {
    id: 10366,
    productName: 'Clutcher Clip for Women | Secure Grip Hair Accessory',
    marketplace: 'Meesho',
    images: [
      { slot: 'Main', url: '/showcase/10366-1-a4815fb8d628225b3075a407e5be310a.webp' },
      { slot: 'Lifestyle', url: '/showcase/10366-2-aba4191d192bb8555d5525a048a43f0d.webp' },
      { slot: 'Features', url: '/showcase/10366-3-1a2deb768af3cf18997d968d0e337d75.webp' },
      { slot: 'Detail', url: '/showcase/10366-4-b5dd62f8ff15027867147efa31edfa39.webp' }
    ],
    fields: [['Category', 'Hair Accessories'], ['Material', 'ABS Plastic'], ['Net Quantity', '1'], ['Ideal For', 'Women']]
  },
  {
    id: 9951,
    productName: 'Magnetic Pencil Box for Kids | Space Theme with Compass',
    marketplace: 'Meesho',
    images: [
      { slot: 'Main', url: '/showcase/9951-1-7c1c9f40077681d7255d7989932eb684.webp' },
      { slot: 'Lifestyle', url: '/showcase/9951-2-a1c6bda97a274457f599097b2ef2b908.webp' },
      { slot: 'Features', url: '/showcase/9951-3-c7d9c211b450c1cbe53e43b46466aba6.webp' },
      { slot: 'Detail', url: '/showcase/9951-4-68dab049995e8063ffa8252d4377f9aa.webp' }
    ],
    fields: [['Category', 'Pencil Cases'], ['Material', 'Plastic'], ['Net Quantity', '1'], ['Ideal For', 'Kids']]
  },
  {
    id: 10365,
    productName: 'Mini Sewing Kit for Home | Compact Travel Stitching Set',
    marketplace: 'Meesho',
    images: [
      { slot: 'Main', url: '/showcase/10365-3-1785872024883-604928717.webp' },
      { slot: 'Lifestyle', url: '/showcase/10365-3-1785872024890-813698657.webp' },
      { slot: 'Features', url: '/showcase/10365-3-1785872024896-498531962.webp' },
      { slot: 'Detail', url: '/showcase/10365-3-1785872024903-603085543.webp' }
    ],
    fields: [['Category', 'Sewing Sets & Kits'], ['Material', 'Mixed'], ['Net Quantity', '1'], ['Ideal For', 'Home']]
  },
  {
    id: 10277,
    productName: 'Makeup Brush Set with Bag | 8 Pieces for Everyday Use',
    marketplace: 'Meesho',
    images: [
      { slot: 'Main', url: '/showcase/10277-3-1785872017482-303802027.webp' },
      { slot: 'Lifestyle', url: '/showcase/10277-3-1785872017489-810850069.webp' },
      { slot: 'Features', url: '/showcase/10277-3-1785872017496-188310349.webp' },
      { slot: 'Detail', url: '/showcase/10277-3-1785872017502-594644595.webp' }
    ],
    fields: [['Category', 'Makeup Accessories'], ['Material', 'Synthetic'], ['Net Quantity', '8'], ['Ideal For', 'Women']]
  }
];

// The single-product shape, kept so nothing that still imports it breaks.
export const SHOWCASE = SHOWCASES[0];
